import {
  // eslint-disable-next-line no-restricted-imports
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  // eslint-disable-next-line no-restricted-imports
  Typography,
  withStyles,
} from '@material-ui/core';
import { ExpandLess as AscIcon, ExpandMore as DescIcon } from '@material-ui/icons';
import React from 'react';
import { SortOrder, SortState, useSortSettings } from '../../hooks/useSortSettings';
import { ContentActions } from './ContentActions';
import { ContentDate } from './ContentDate';
import { ContentIcon } from './ContentIcon';
import type { ContentViewProps } from './ContentViewItem';
import { useDragAndDropEvents } from './useDragAndDropEvents';

interface HeadCellInnerProps extends Omit<SortState, 'sortedContent'> {
  field: string;
}

export const HeadCellInner: React.FC<HeadCellInnerProps> = ({
  field,
  children,
  sortOrder,
  sortField,
}) => {
  const ariaSort =
    sortField === field ? (sortOrder === 'asc' ? 'ascending' : 'descending') : undefined;

  return (
    <Typography variant='h5' aria-sort={ariaSort}>
      {children}
      {sortField === field && sortOrder === 'asc' && <AscIcon role='img' />}
      {sortField === field && sortOrder === 'desc' && <DescIcon role='img' />}
    </Typography>
  );
};

const ClickableTableCell = withStyles({
  root: {
    cursor: 'pointer',
  },
})(TableCell);

const StyledTable = withStyles({
  root: {
    minWidth: 550,
  },
})(Table);

const toggleHeader = (order: SortOrder, isActive: boolean): SortOrder =>
  isActive ? (order === 'asc' ? 'desc' : 'asc') : 'desc';

export const ContentTable: React.FC<ReturnType<typeof useSortSettings>> = ({
  children,
  sortOrder,
  sortField,
  setSort,
}) => (
  <TableContainer component={Paper}>
    <StyledTable size='small'>
      <TableHead>
        <TableRow>
          <TableCell>&nbsp;</TableCell>
          <ClickableTableCell
            onClick={() =>
              setSort({
                sortField: 'name',
                sortOrder: toggleHeader(sortOrder, sortField === 'name'),
              })
            }
          >
            <HeadCellInner field='name' sortOrder={sortOrder} sortField={sortField}>
              Name
            </HeadCellInner>
          </ClickableTableCell>
          <TableCell>&nbsp;</TableCell>
          <ClickableTableCell
            onClick={() =>
              setSort({
                sortField: 'type',
                sortOrder: toggleHeader(sortOrder, sortField === 'type'),
              })
            }
          >
            <HeadCellInner field='type' sortOrder={sortOrder} sortField={sortField}>
              Type
            </HeadCellInner>
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() =>
              setSort({
                sortField: 'modifiedAt',
                sortOrder: toggleHeader(sortOrder, sortField === 'modifiedAt'),
              })
            }
          >
            <HeadCellInner field='modifiedAt' sortOrder={sortOrder} sortField={sortField}>
              Last Change
            </HeadCellInner>
          </ClickableTableCell>
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </StyledTable>
  </TableContainer>
);

const StyledTableRow = withStyles({
  root: {
    '&:hover': {
      backgroundColor: '#E8E8E8',
    },
  },
})(TableRow);

export const ContentListItem: React.FC<ContentViewProps> = ({
  content,
  openContent,
  contentDisplayName,
  thumbnail,
  ...actionConfigs
}) => {
  const dragAndDropProps = useDragAndDropEvents(content);

  return (
    <StyledTableRow {...dragAndDropProps}>
      <ClickableTableCell scope='row' onClick={openContent}>
        <ContentIcon thumbnail={thumbnail} displayName={contentDisplayName} />
      </ClickableTableCell>
      <ClickableTableCell onClick={openContent}>
        <Typography>{content.name}</Typography>
      </ClickableTableCell>
      <TableCell>
        <ContentActions {...actionConfigs} />
      </TableCell>
      <TableCell>
        <Typography>{contentDisplayName}</Typography>
      </TableCell>
      <TableCell>
        <ContentDate content={content} />
      </TableCell>
    </StyledTableRow>
  );
};
